import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, UserCog, Info } from "lucide-react";
import { roleChangeService } from "@/services/roleChangeService";

interface RoleChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentRole: string;
  onSuccess?: () => void;
}

const RoleChangeDialog = ({
  open,
  onOpenChange,
  currentRole,
  onSuccess,
}: RoleChangeDialogProps) => {
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Get available roles for selection
  const availableRoles = roleChangeService.getAvailableRoles(currentRole);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedRole) {
      toast({
        title: "Validation Error",
        description: "Please select a role to request.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      await roleChangeService.createRequest({
        requestedRole: selectedRole,
        reason: reason.trim() || undefined,
      });

      toast({
        title: "Request Submitted",
        description: "Your role change request has been submitted successfully. An admin will review your request.",
      });

      // Reset form
      setSelectedRole("");
      setReason("");
      
      // Close dialog
      onOpenChange(false);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error creating role change request:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to submit request";

      toast({
        title: "Request Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!loading) {
      setSelectedRole("");
      setReason("");
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-full max-w-[90vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-light">
            <UserCog className="w-5 h-5" />
            Request Role Change
          </DialogTitle>
          <DialogDescription className="font-light">
            Submit a request to change your current role. An administrator will review and approve your request.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Role Display */}
          <div className="space-y-2">
            <Label className="font-light">Current Role</Label>
            <div className="p-3 bg-muted rounded-md">
              <span className="font-medium">
                {currentRole?.replace("_", " ") || "Unknown"}
              </span>
            </div>
          </div>

          {/* Role Selection */}
          <div className="space-y-2">
            <Label htmlFor="requestedRole" className="font-light">
              Requested Role <span className="text-destructive">*</span>
            </Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role to request" />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason/Justification */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="font-light">
              Reason for Request (Optional)
            </Label>
            <Textarea
              id="reason"
              placeholder="Please provide a brief explanation for why you need this role change..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={4}
              className="resize-none font-light"
            />
            <p className="text-xs text-muted-foreground">
              {reason.length}/500 characters
            </p>
          </div>

          {/* Info Message */}
          <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md">
            <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-blue-900 dark:text-blue-100">
                Review Process
              </p>
              <p className="text-blue-700 dark:text-blue-200 font-light mt-1">
                Your request will be reviewed by an administrator. You'll receive a notification once a decision has been made.
              </p>
            </div>
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="font-medium"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !selectedRole}
              className="font-medium"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <UserCog className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RoleChangeDialog;